// src/services/authService.ts
import { API_BASE_URL } from "@/config";

/**
 * Get authentication headers for API requests
 * @returns Headers object with Authorization header
 */
export function getAuthHeaders() {
  const token = localStorage.getItem("access_token");
  return {
    Authorization: token ? `Bearer ${token}` : "",
  };
}

/**
 * Login with email and password
 * @param email User email
 * @param password User password
 * @returns Session, user, and role information
 */
export async function login(email: string, password: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password }),
      credentials: 'include' // Include cookies in the request
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: `Error ${response.status}: ${response.statusText}` }));
      throw new Error(error.message || 'Login failed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Login error details:', error);
    throw error;
  }
}

/**
 * Register a new user
 * @param email User email
 * @param password User password
 * @param displayName User display name
 * @param inviteCode Invite code
 * @returns Session, user, and role information
 */
export async function register(email: string, password: string, displayName: string, inviteCode: string) {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, password, displayName, inviteCode })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Registration failed');
  }
  
  return await response.json();
}

type RegistrationAvailabilityPayload = {
  email?: string;
  displayName?: string;
};

export async function checkRegistrationAvailability(
  payload: RegistrationAvailabilityPayload,
  signal?: AbortSignal
) {
  const response = await fetch(`${API_BASE_URL}/auth/register/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.message || 'Unable to check availability');
  }

  return await response.json();
}

/**
 * Logout the current user
 */
export async function logout() {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Logout failed');
  }
  
  return await response.json();
}

/**
 * Get the current session
 * @returns Session, user, and role information
 */
export async function getSession() {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/session`, {
      headers: getAuthHeaders(),
      credentials: 'include' // Include cookies in the request
    });
    
    if (!response.ok) {
      return { session: null, user: null, role: null };
    }
    
    return await response.json();
  } catch (error) {
    console.error('Session fetch error:', error);
    return { session: null, user: null, role: null };
  }
}

/**
 * Request a password reset
 * @param email User email
 */
export async function resetPassword(email: string) {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Password reset failed');
  }
  
  return await response.json();
}

/**
 * Update the user's password
 * @param password New password
 * @param token Optional password reset token (from Supabase)
 */
export async function updatePassword(password: string, token?: string) {
  const response = await fetch(`${API_BASE_URL}/auth/update-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(
      token
        ? { password, token }
        : { password }
    )
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Password update failed');
  }
  
  return await response.json();
}

/**
 * Check if the current user has a specific role
 * @returns Role information
 */
export async function getUserRole() {
  const response = await fetch(`${API_BASE_URL}/auth/session`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    return null;
  }
  
  return await response.json();
} 
